import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';

interface AvatarSelectorProps {
  value: string;
  onChange: (url: string) => void;
}

// Avatar style options
const AVATAR_STYLES = [
  { id: 'avataaars', name: 'Avataaars', demo: 'https://api.dicebear.com/7.x/avataaars/svg?seed=demo1' },
  { id: 'bottts', name: 'Bottts', demo: 'https://api.dicebear.com/7.x/bottts/svg?seed=demo1' },
  { id: 'pixel-art', name: 'Pixel Art', demo: 'https://api.dicebear.com/7.x/pixel-art/svg?seed=demo1' },
  { id: 'identicon', name: 'Identicon', demo: 'https://api.dicebear.com/7.x/identicon/svg?seed=demo1' },
  { id: 'thumbs', name: 'Thumbs', demo: 'https://api.dicebear.com/7.x/thumbs/svg?seed=demo1' }
];

const AvatarSelector: React.FC<AvatarSelectorProps> = ({ value, onChange }) => {
  const [selectedStyle, setSelectedStyle] = useState(AVATAR_STYLES[0].id);
  const [seed, setSeed] = useState(() => Math.random().toString(36).substring(7));

  const generateAvatar = (style: string, newSeed?: string) => {
    const seedToUse = newSeed || seed;
    const url = `https://api.dicebear.com/7.x/${style}/svg?seed=${seedToUse}`;
    onChange(url);
  };

  const handleStyleChange = (style: string) => {
    setSelectedStyle(style);
    generateAvatar(style);
  };

  const handleRandomize = () => {
    const newSeed = Math.random().toString(36).substring(7);
    setSeed(newSeed);
    generateAvatar(selectedStyle, newSeed);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative">
          <img
            src={value || AVATAR_STYLES[0].demo}
            alt="Selected avatar"
            className="w-20 h-20 rounded-lg border-2 border-gray-800"
          />
          <button
            type="button" // Add type="button" to prevent form submission
            onClick={handleRandomize}
            className="absolute -bottom-2 -right-2 p-2 bg-brand hover:bg-brand-dark text-white rounded-full transition-colors"
            title="Generate new random avatar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium mb-2">Avatar Style</label>
          <select
            value={selectedStyle}
            onChange={(e) => handleStyleChange(e.target.value)}
            className="w-full px-3 py-2 bg-background border border-gray-800 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
          >
            {AVATAR_STYLES.map(style => (
              <option key={style.id} value={style.id}>
                {style.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-2">
        {AVATAR_STYLES.map(style => (
          <button
            key={style.id}
            type="button" // Add type="button" to prevent form submission
            onClick={() => handleStyleChange(style.id)}
            className={`p-2 rounded-lg border-2 transition-all ${
              selectedStyle === style.id
                ? 'border-brand shadow-[0_0_15px_rgba(62,207,142,0.3)]'
                : 'border-gray-800 hover:border-gray-700'
            }`}
          >
            <img
              src={style.demo}
              alt={style.name}
              className="w-full aspect-square rounded"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default AvatarSelector;